import React from 'react';
import { withMobileContext } from '@wix/da-react-context/pkg/MobileContext';
import LazyContext from '@wix/da-shared-react/pkg/LazyLoadingImage/LazyContext';

import withSectionFetchSpinner from '../../../decorators/withSectionFetchSpinner';
import { ModuleType } from '../../../../types/modules';
import Zone, { ZoneGroup } from '../../Layout/Zone';
import { ZoneType } from '../../../../types/layout';

import { AboutMeBox } from '../../UserWidgets/AboutMe';
import { WatchersBox, WatchingBox } from '../../UserWidgets/WatchedDeviants';
import DonationsBox from '../../UserWidgets/Donations';
import SupportersBox from '../../UserWidgets/Supporters';
import FeaturedDeviation from '../../UserWidgets/FeaturedDeviation';
import FeaturedPost from '../../UserWidgets/FeaturedPost';
import StripExhibitionist from '../../UserWidgets/StripExhibitionist';
import TallExhibitionist from '../../UserWidgets/TallExhibitionist';
import { GroupsAdminBox, GroupsMemberBox } from '../../UserWidgets/Groups';
import ProfileCommentsBox from '../../UserWidgets/ProfileComments';
import DeviousnessBox from '../../UserWidgets/Deviousness';
import CustomBox from '../../UserWidgets/Custom';
import MegaboxBox from '../../UserWidgets/Megabox';
import FeedBox from '../../UserWidgets/Feed';
import AdBox from '../../UserWidgets/Ad';
import TiersBox from '../../UserWidgets/Tiers/PromotionBox';
import BadgesSpotlightBox from '../../UserWidgets/BadgesSpotlight';
import LlamaFarmBox from '../../UserWidgets/LlamaFarm';

import {
  MembersBox,
  ActiveMembersBox,
  NewestMembersBox,
} from '../../GroupWidgets/UserStream';
import AdminsBox from '../../GroupWidgets/Admins';
import GroupAboutBox from '../../GroupWidgets/GroupAbout';

import s from './HomeSection.scss';

const mapModuleTypeToWidgetComponent = {
  [ModuleType.ABOUT]: AboutMeBox,
  [ModuleType.EXHIBITIONIST]: TallExhibitionist,
  [ModuleType.FEATURED_DEVIATION]: FeaturedDeviation,
  [ModuleType.FEATURED_POST]: FeaturedPost,
  [ModuleType.WATCHERS]: WatchersBox,
  [ModuleType.WATCHING]: WatchingBox,
  [ModuleType.SUPPORTERS]: SupportersBox,
  [ModuleType.GROUPS_ADMIN]: GroupsAdminBox,
  [ModuleType.GROUPS_MEMBER]: GroupsMemberBox,
  [ModuleType.ACTIVE_MEMBERS]: ActiveMembersBox,
  [ModuleType.NEWEST_MEMBERS]: NewestMembersBox,
  [ModuleType.COMMENTS]: ProfileCommentsBox,
  [ModuleType.DONATIONS]: DonationsBox,
  [ModuleType.DEVIOUSNESS]: DeviousnessBox,
  [ModuleType.FEED]: FeedBox,
  [ModuleType.CUSTOM]: CustomBox,
  [ModuleType.TIERS]: TiersBox,
  [ModuleType.BADGES_SPOTLIGHT]: BadgesSpotlightBox,
  [ModuleType.LLAMA_FARM]: LlamaFarmBox,

  [ModuleType.GROUP_ADMINS]: AdminsBox,
  [ModuleType.GROUP_MEMBERS]: MembersBox,
  [ModuleType.GROUP_ABOUT]: GroupAboutBox,
};

export interface Props {
  isMobile?: boolean;
  isOwner?: boolean;
  hasCore?: boolean;
  hasMegaboxInstalled?: boolean;
  hasAboutMeInstalled?: boolean;
}

class HomeSection extends React.Component<Props> {
  render() {
    const isMobile = this.props.isMobile;
    const zoneProps = {
      className: s['zone'],
      mapModuleTypeToWidgetComponent,
    };
    return (
      <div className={s['root']}>
        <LazyContext.Provider value={true}>
          {isMobile
            ? this.renderOneColumn(zoneProps)
            : this.renderTwoColumns(zoneProps)}
        </LazyContext.Provider>
      </div>
    );
  }

  renderTwoColumns(zoneProps) {
    const { hasMegaboxInstalled, hasAboutMeInstalled } = this.props;
    return (
      <>
        <div className={s['zone-top']}>
          {/*
            If the MegaBox is installed, we show it in a separate
            ZoneGroup, so that it does not merge the AddWidgets with the Zone
            below it and to ensure it is shown on top. If it is not installed on
            the other hand, we want to collapse the Zone and merge the AddWidgets
            from the MEGA_BAR and the TOP_BAR zones into one, that's why we have
            them as the single ZoneGroup if Megabox is not installed.
          */}
          {!hasMegaboxInstalled && (
            <ZoneGroup>
              <Zone
                type={ZoneType.MEGA_BAR}
                mapModuleTypeToWidgetComponent={{
                  [ModuleType.MEGABOX]: MegaboxBox,
                }}
                direction="row"
              />
              <Zone
                type={ZoneType.TOP_BAR}
                mapModuleTypeToWidgetComponent={{
                  [ModuleType.EXHIBITIONIST]: StripExhibitionist,
                  [ModuleType.TIERS]: TiersBox,
                  [ModuleType.BADGES_SPOTLIGHT]: BadgesSpotlightBox,
                }}
              />
            </ZoneGroup>
          )}
          {hasMegaboxInstalled && (
            <>
              <Zone
                type={ZoneType.MEGA_BAR}
                mapModuleTypeToWidgetComponent={{
                  [ModuleType.MEGABOX]: MegaboxBox,
                }}
                direction="row"
              />
              <ZoneGroup>
                <Zone
                  type={ZoneType.TOP_BAR}
                  mapModuleTypeToWidgetComponent={{
                    [ModuleType.EXHIBITIONIST]: StripExhibitionist,
                    [ModuleType.TIERS]: TiersBox,
                    [ModuleType.BADGES_SPOTLIGHT]: BadgesSpotlightBox,
                  }}
                />
              </ZoneGroup>
            </>
          )}
        </div>
        <div className={s['flex-wrap']}>
          <div className={s['zone-column-left']}>
            {/*
            In order for the top widgets from the left and right columns to be
            aligned, we need both of them to either show or hide the top
            AddWidget. When AboutMe is installed, then we disable the top
            AddWidget in the right column, to ensure that the AboutMe widget is
            always rendered as the first widget in the column.
            */}
            <ZoneGroup disableTopAddWidget={hasAboutMeInstalled}>
              <Zone type={ZoneType.TOP_LEFT} {...zoneProps} />
              <Zone type={ZoneType.BOTTOM_LEFT} {...zoneProps} />
            </ZoneGroup>
          </div>
          <div className={s['zone-column-right']}>
            {/*
              Similarly to the Megabox case up there, we do the same for AboutMe box.
            */}
            {hasAboutMeInstalled && (
              <ZoneGroup>
                <Zone type={ZoneType.TOP_RIGHT} {...zoneProps} />
                {this.props.isOwner && <AdBox />}
              </ZoneGroup>
            )}
            <ZoneGroup>
              {!hasAboutMeInstalled && (
                <Zone type={ZoneType.TOP_RIGHT} {...zoneProps} />
              )}
              <Zone type={ZoneType.MID_RIGHT} {...zoneProps} />
              <Zone type={ZoneType.BOTTOM_RIGHT} {...zoneProps} />
            </ZoneGroup>
          </div>
        </div>
      </>
    );
  }

  renderOneColumn(zoneProps) {
    return (
      <div className={s['zone-wrapper']}>
        <div className={s['zone-top']}>
          <ZoneGroup disableTopAddWidget={true} className={s['zone-group']}>
            <Zone type={ZoneType.TOP_RIGHT} {...zoneProps} locked={false} />
            <Zone
              type={ZoneType.MEGA_BAR}
              mapModuleTypeToWidgetComponent={{
                [ModuleType.MEGABOX]: MegaboxBox,
              }}
              direction="row"
            />
          </ZoneGroup>
        </div>
        <ZoneGroup className={s['zone-group']}>
          <Zone type={ZoneType.TOP_BAR} {...zoneProps} />
          <Zone type={ZoneType.TOP_LEFT} {...zoneProps} />
          <Zone type={ZoneType.MID_RIGHT} {...zoneProps} />
          <Zone type={ZoneType.BOTTOM_LEFT} {...zoneProps} />
          <Zone type={ZoneType.BOTTOM_RIGHT} {...zoneProps} />
        </ZoneGroup>
      </div>
    );
  }
}

export default withMobileContext(withSectionFetchSpinner(HomeSection));
